import React from 'react';
import { Card, ListGroup, Image, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { safeFormatDate, formatDateWithLocale, normalizeLocation, sanitizeHTML } from '../../shared/utils';
import { getLinkIconDisplay } from '../../shared/linkIcons';

const PrintDay = ({ day, dayNumber }) => {
  const { t } = useTranslation();
  const { date, activities = [] } = day;

  // Helper to translate activity type
  const translateActivityType = (type) => {
    if (!type) return '';
    if (type === 'accommodation') {
      return t('planDetail.days.accommodation');
    }
    if (type === 'transportation') {
      return t('planDetail.days.transportation');
    }
    if (type === 'attraction') {
      return t('planDetail.days.attraction');
    }
    return type; // Fallback to original type
  };

  // Special handling for links in PDFs - use proper full URLs
  const getFullUrl = (url) => {
    if (!url) return '#';
    
    // If already has http/https, return as is
    if (url.match(/^https?:\/\//i)) {
      return url;
    }
    
    // If it starts with www, add https://
    if (url.match(/^www\./i)) {
      return 'https://' + url;
    }
    
    // Remove any leading slashes and add https://
    return 'https://' + url.replace(/^\/+/, '');
  };

  return (
    <div className="print-day mb-4">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">
              {t('planDetail.print.day')} {dayNumber}: {formatDateWithLocale(date, 'PPPP')}
            </h3>
          </div>          
        </Card.Header>
        
        <Card.Body>
          {/* Day summary (title + text) - visible in print and PDF */}
          {(day.summaryTitle?.trim() || day.summary?.trim()) && (
            <div className="print-day-summary mb-3 p-2 rounded bg-light">
              {day.summaryTitle?.trim() && (
                <h6 className="mb-1 fw-semibold">{day.summaryTitle.trim()}</h6>
              )}
              {day.summary?.trim() && (
                <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{day.summary.trim()}</p>
              )}
            </div>
          )}

          {day.description && (
            <div className="print-day-description info-content mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHTML(day.description) }} />
          )}
          
          {activities.length > 0 ? (
            <div className="activities-list">
              <ListGroup variant="flush">
                {activities.map((activity, index) => (
                  <ListGroup.Item 
                    key={activity.id || index} 
                    className="border-bottom pb-3 mb-3 activity-item"
                    style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="activity-content w-100">
                        <div className="d-flex justify-content-between mb-1">
                          <h5 className="mb-0">{activity.name || t('planDetail.print.untitledActivity')}</h5>
                          {activity.type && (
                            <Badge bg="secondary" className="ms-2">{translateActivityType(activity.type)}</Badge>
                          )}
                        </div>
                        
                        {/* Time information - only show the time part */}
                        {activity.startDateTime && (
                          <div className="activity-time mb-2">
                            <i className="bi bi-clock me-1"></i>
                            {safeFormatDate(activity.startDateTime,'time')}
                            {activity.endDateTime && activity.type !== 'accommodation' && 
                              ` - ${safeFormatDate(activity.endDateTime,'time')}`}
                          </div>
                        )}
                        
                        {/* Special accommodation fields */}
                        {activity.type === 'accommodation' && (
                          <div className="accommodation-details mb-2">
                            {activity.checkInTime && (
                              <div className="mb-1">
                                <strong>Check-in time:</strong> {activity.checkInTime}
                              </div>
                            )}
                            {activity.checkOutTime && (
                              <div className="mb-1">
                                <strong>Check-out time:</strong> {activity.checkOutTime}
                              </div>
                            )}
                            {activity.endDateTime && (
                              <div className="mb-1">
                                <strong>{t('planDetail.print.checkOutDate')}</strong> {safeFormatDate(activity.endDateTime, 'yyyy-MM-dd')}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {activity.location && (
                          <div className="activity-location mb-2">
                            <i className="bi bi-geo-alt me-1"></i>
                            {normalizeLocation(activity.location).address}
                          </div>
                        )}
                        
                        {activity.description && (
                          <p className="mb-2">{activity.description}</p>
                        )}
                        
                        {/* Activity Notes */}
                        {activity.notes && (
                          <div className="activity-notes mb-2">
                            <strong>{t('planDetail.print.notes')}</strong>
                            <p className="mb-0">{activity.notes}</p>
                          </div>
                        )}
                        
                        {/* Activity Images */}
                        {activity.images && Array.isArray(activity.images) && activity.images.length > 0 && (
                          <div className="activity-images mt-3 mb-2">
                            <div className="d-flex flex-wrap">
                              {activity.images.map((img, imgIdx) => (
                                <div key={imgIdx} className="me-2 mb-2" style={{ maxWidth: '200px' }}>
                                  {img.url && (
                                    <Image 
                                      src={img.url} 
                                      thumbnail 
                                      className="activity-image" 
                                      style={{ maxHeight: '120px', objectFit: 'cover' }}
                                      alt={img.type || 'Activity image'}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Activity Links as plain text with URLs - better for PDF */}
                        {activity.links && Array.isArray(activity.links) && activity.links.length > 0 && (
                          <div className="activity-links mt-3 mb-2">
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              {activity.links.map((link, idx) => {
                                const iconDisplay = getLinkIconDisplay(link);
                                return (
                                  <a 
                                    key={idx}
                                    href={getFullUrl(link.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="badge bg-light text-dark text-decoration-none border pdf-link d-inline-flex align-items-center gap-1"
                                    style={{ padding: '0.2rem 0.45rem' }}
                                  >
                                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '1.5em', height: '1.5em' }}>
                                      {iconDisplay.iconUrl ? (
                                        <img src={iconDisplay.iconUrl} alt="" style={{ width: '1.5em', height: '1.5em', objectFit: 'contain' }} />
                                      ) : (
                                        <i className={`bi ${iconDisplay.iconClass || 'bi-link-45deg'}`} style={{ fontSize: '1.5em', lineHeight: 1 }}></i>
                                      )}
                                    </span>
                                    <span>{link.name || 'Link'}</span>
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Cost Badge - when cost has a value (including 0); empty = don't show */}
                        {activity.cost && activity.cost.value != null && activity.cost.value !== '' && (
                          <div className="d-flex justify-content-end mt-2">
                            <Badge bg="light" text="dark">
                              {activity.cost.currency} {activity.cost.value}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          ) : (
            <p className="text-muted">{t('planDetail.print.noActivitiesPlanned')}</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PrintDay; 